import React, { memo, useEffect } from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { useFormikContext } from 'formik'
import { Button } from 'src/components/shared/Button'
import type { OAuthClientFormMode, OAuthClientFormValues } from './OAuthClientForm.interface'
import { OAuthScopesField } from './OAuthScopesField'

/**
 * There is no rotate-secret endpoint, no DELETE, and POST 400s once a client exists — so a
 * client secret genuinely cannot be regenerated. The control is kept from the approved
 * prototype but permanently disabled, so the gap is visible to admins instead of silently
 * absent. Remove the `disabled` and wire it up if a rotate endpoint ships.
 */
const REGENERATE_DISABLED_HINT =
  'No rotate endpoint exists yet. A client secret is shown only once, when the client is created, and cannot be regenerated.'

interface Props {
  mode: OAuthClientFormMode
  // Without `oauth_clients_manage`: scopes disabled and no submit. The Regenerate stub is unchanged.
  isReadOnly: boolean
  isSaving: boolean
  onDirtyChange(isDirty: boolean): void
}

const OAuthClientFormContentComponent = ({ mode, isReadOnly, isSaving, onDirtyChange }: Props) => {
  const { dirty } = useFormikContext<OAuthClientFormValues>()

  // Lifted so OAuthStatusBar — which sits outside this Formik tree — can disable its
  // Enable/Disable button while scope edits are unsaved.
  useEffect(() => {
    onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  return (
    <div css={tw`flex flex-col gap-4`}>
      <OAuthScopesField disabled={isReadOnly} />
      <div css={tw`flex flex-row items-center justify-between gap-2`}>
        {mode === 'manage' ? (
          <Tooltip title={REGENERATE_DISABLED_HINT}>
            <span>
              <Button
                size="small"
                color="white"
                disabled
                data-testid="oauth-regenerate-secret-button"
              >
                Regenerate secret
              </Button>
            </span>
          </Tooltip>
        ) : (
          <span />
        )}
        {isReadOnly ? null : (
          <Button
            type="submit"
            size="small"
            color="dark"
            className={tw`px-4`}
            loading={isSaving}
            disabled={isSaving}
            data-testid="oauth-client-submit-button"
          >
            {mode === 'create' ? 'Create API client' : 'Save changes'}
          </Button>
        )}
      </div>
    </div>
  )
}

export const OAuthClientFormContent = memo(OAuthClientFormContentComponent)
